import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Loader2 } from 'lucide-react';

/**
 * Lets a user into the agency workspace if they are an agency owner
 * (role 'agency'/'agency_staff') OR have an active agency membership
 * (covers existing accounts linked as staff, whatever their primary role).
 * The backend enforces the real scoping; this only unblocks the route.
 */
export const AgencyWorkspaceRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [state, setState] = useState('checking');

  useEffect(() => {
    let alive = true;
    if (loading) return;
    if (!user) { setState('nologin'); return; }
    // Owner (role 'agency') owns the workspace; staff still need an active
    // membership, so a deactivated staff account is shown "access removed".
    if (user.role === 'agency') { setState('ok'); return; }
    api.get('/agency/staff/my-access')
      .then(() => alive && setState('ok'))
      .catch(() => alive && setState('denied'));
    return () => { alive = false; };
  }, [user, loading]);

  if (loading || state === 'checking') {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>;
  }
  if (state === 'nologin') return <Navigate to="/login" state={{ from: location }} replace />;
  if (state === 'denied') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="agency-access-removed">
        <div className="text-center max-w-sm">
          <h2 className="text-lg font-semibold text-slate-800 mb-1">Workspace access removed</h2>
          <p className="text-sm text-slate-500">Your access to this agency workspace has been removed. Please contact the agency owner if you believe this is a mistake.</p>
        </div>
      </div>
    );
  }
  return children;
};

export default AgencyWorkspaceRoute;
